import FastModal from 'components/FastModal';
import './ManualPlateTypeEditor.scss';
import {FmlxCheckBox} from 'fmlx-common-ui';
import FormattedHTMLMessage from 'components/FormattedHTMLMessage';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {Labware} from 'components';
import {find} from 'lib/objectHelper';
import {Orientation, WELL_HOVER_INDEX_COLOR_POSITION, WellCount} from 'constants/dataEnum';
import {WELL_HOVER_MODE} from 'components/Labware';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  checkIs24Or48WellLabware,
  getSkipWellMode,
  initSwipingInfo,
  normalizeSwipingInfo,
  updateSwipingInfo,
} from 'contents/ProtocolEditor/VirtualTray/labwareHelper';
import {getWellStyleColor} from 'stores/business/labwareBusinessAction';
import {useIntl} from 'react-intl';
import {setManualReteachCurrentInfo, setSelectedWellStyle} from 'stores/actions/reteachPlateAction';

const hoverWellColorEnum = '#50B46D';
const IFCIgnoredIndexes = [9, 10, 11, 12];

function ModalPreparationManualTypePlateEditor({showModal, onConfirm, onClose, selectedWellStyle}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const id = useSelector((state) => state.plateTypeEditorState.id);
  const deepWellThreshold = useSelector((state) => state.configState.deepWellThreshold);
  const availableLabwareState = useSelector((state) => state.availableLabwareState);
  const labware = find(availableLabwareState, {LabwareID: id});
  const [isPlacedOnTheTray, setIsPlacedOnTheTray] = useState(false);
  const swipingInfoRef = useRef();

  const allowConfirm = useMemo(
    () => isPlacedOnTheTray && Object.keys(selectedWellStyle).length > 0,
    [isPlacedOnTheTray, selectedWellStyle],
  );

  const {LabwareID, colCount, rowCount, isWellRectangle, isIFC, labwareIndexes} = labware;

  const button = [
    {
      className: 'btn-lg',
      style: 'primary',
      disabled: !allowConfirm,
      handlerClick: () => {
        onConfirm();
      },
      text: intl.formatMessage({id: 'PreparationManualPlateTypeEditor.modal.firstButton'}),
    },
    {
      className: 'btn-lg',
      style: 'secondary',
      handlerClick: () => {
        onClose();
        dispatch(setSelectedWellStyle({}));
      },
      text: intl.formatMessage({id: 'PreparationManualPlateTypeEditor.modal.secondButton'}),
    },
  ];

  const isLabware384 = () => {
    const labwareWellCount = colCount * rowCount;

    return labwareWellCount >= WellCount.WELL_384;
  };

  useEffect(() => {
    if (!Object.keys(selectedWellStyle).length > 0) setIsPlacedOnTheTray(false);
  }, [selectedWellStyle]);

  function onHandleSwipeStart(itemID) {
    const [row, column, labwareIndex] = itemID.split('-');
    const [normalizedRow, normalizedColumn] = [row, column];
    if (labware.isIFC && IFCIgnoredIndexes.includes(Number(labwareIndex))) return;

    swipingInfoRef.current = initSwipingInfo(Number(normalizedRow), Number(normalizedColumn), labware, false);
    swipingInfoRef.current.originStart = {
      row: normalizedRow,
      column: normalizedColumn,
      labwareIndex,
    };
    const temp = dispatch(
      getWellStyleColor(
        LabwareID,
        Number(normalizedRow),
        Number(normalizedColumn),
        hoverWellColorEnum,
        1,
        1,
        null,
        false,
        null,
        null,
        0,
        0,
        true,
        true,
        labwareIndex,
        true,
      ),
    );
    dispatch(setSelectedWellStyle(temp));
  }

  const onHandleSwipeMove = (itemID, swipingDirectionY, swipingDirectionX, isCtrl, isShift) => {
    const isSwipeAllowed = checkIs24Or48WellLabware(labware);
    if (isSwipeAllowed) return;
    if (!swipingInfoRef.current) {
      return;
    }

    const {swipingStart, originStart} = swipingInfoRef.current;
    const [row, column, labwareIndex] = itemID.split('-');

    if (originStart?.labwareIndex !== labwareIndex) {
      return;
    }

    if (labware.isIFC && IFCIgnoredIndexes.includes(Number(labwareIndex))) return;

    const [skipWellMode, noSkip] = getSkipWellMode(null, null, isShift, isLabware384());

    const newSwipingInfo = updateSwipingInfo(
      swipingInfoRef.current,
      {row: 1, column: 1},
      {row: swipingStart.row, column: swipingStart.column},
      {row: Number(row), column: Number(column)},
      swipingDirectionY,
      swipingDirectionX,
      [],
    );

    if (newSwipingInfo) {
      swipingInfoRef.current = {
        ...swipingInfoRef.current,
        ...newSwipingInfo,
      };
    }

    const [normalizedSwipingStart, , normalizedSwipingSize, skipWell] = normalizeSwipingInfo(
      swipingInfoRef.current,
      skipWellMode,
      noSkip,
      labware.isIFC,
    );

    if (normalizedSwipingStart && normalizedSwipingSize) {
      dispatch(
        setSelectedWellStyle(
          dispatch(
            getWellStyleColor(
              LabwareID,
              normalizedSwipingStart.row,
              normalizedSwipingStart.column,
              hoverWellColorEnum,
              normalizedSwipingSize.row,
              normalizedSwipingSize.column,
              null,
              false,
              null,
              null,
              skipWell?.row,
              skipWell?.column,
              true,
              noSkip,
              labwareIndex,
              true,
            ),
          ),
        ),
      );
    }
  };

  const onHandleSwipeEnd = () => {
    dispatch(setManualReteachCurrentInfo(swipingInfoRef.current));
    swipingInfoRef.current = null;
  };

  const modalContent = (
    <div className="manual-preparation">
      <div style={{display: 'flex'}}>
        <span className="manual-preparation__number">1. </span>
        <span className="manual-preparation__text">
          {intl.formatMessage({id: 'PreparationManualPlateTypeEditor.modal.firstPoint'})}
        </span>
      </div>

      <div className="manual-preparation__labware">
        <Labware
          ID={`labware-${LabwareID}`}
          colCount={isIFC ? 1 : colCount}
          rowCount={isIFC ? 1 : rowCount}
          orientation={Orientation.POTRAIT}
          isWellRectangle={isWellRectangle}
          showColIdx={isIFC ? false : [1]}
          showRowIdx={isIFC ? false : ['A']}
          wellHoverMode={WELL_HOVER_MODE.INDEX_COLOR}
          wellHoverHighlightPosition={WELL_HOVER_INDEX_COLOR_POSITION.BOTH}
          isIFC={isIFC}
          isDeepWell={labware?.wellDepth > deepWellThreshold}
          IFCvariant={colCount}
          swipeEnable
          onMouseLeave={() => {
            swipingInfoRef.current = null;
          }}
          onSwipeStart={onHandleSwipeStart}
          onSwipeEnd={onHandleSwipeEnd}
          onSwipeMove={onHandleSwipeMove}
          showSelectionLine={false}
          labwareIndexes={labwareIndexes}
          wellStyle={selectedWellStyle || {}}
        />
      </div>
      <div style={{display: 'flex'}}>
        <span className="manual-preparation__number">2. </span>
        <span className="manual-preparation__text">
          <FormattedHTMLMessage id="PreparationManualPlateTypeEditor.modal.secondPoint" />
        </span>
      </div>
      <div className="manual-preparation__checkbox">
        <FmlxCheckBox
          size="sm"
          checked={isPlacedOnTheTray}
          label={intl.formatMessage({id: 'PreparationManualPlateTypeEditor.modal.checkbox.text'})}
          onChange={() => setIsPlacedOnTheTray(!isPlacedOnTheTray)}
        />
      </div>
    </div>
  );

  return (
    <FastModal
      show={showModal}
      btnAction={button}
      header={intl.formatMessage({id: 'PreparationManualPlateTypeEditor.modal.header'})}
      content={modalContent}
      size="md"
    />
  );
}

ModalPreparationManualTypePlateEditor.propTypes = {
  showModal: PropTypes.bool.isRequired,
  selectedWellStyle: PropTypes.object,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

ModalPreparationManualTypePlateEditor.defaultProps = {
  selectedWellStyle: {},
  onConfirm: () => {},
};

export default ModalPreparationManualTypePlateEditor;
